import React, { Component } from 'react';

import PropTypes from 'prop-types';

import { TrackEventContext } from 'common/containers/EventContainer';
import { ShowIntercomContext } from 'common/containers/IntercomContainer';
import { ViewerContext } from 'common/containers/ViewerContainer';
import SalesforceLandingHelmet from 'common/helmets/SalesforceLandingHelmet';
import Button from 'common/inputs/Button';
import LandingFooterCTA from 'common/LandingFooterCTA';
import LazyLoadedImage from 'common/LazyLoadedImage';
import Link from 'common/Link';
import withContexts from 'common/util/withContexts';
import FAQs from 'common/www/Faqs';
import Salesforce1 from 'img/landing/salesforce-1.webp';
import Salesforce2 from 'img/landing/salesforce-2.webp';
import Salesforce3 from 'img/landing/salesforce-3.webp';

import SalesforceLogo from 'img/canny-salesforce-integration.png';

import 'css/components/integrations/_IntegrationsLanding.scss';
import 'css/components/_LandingFeatures.scss';

class SalesforceLanding extends Component {
  static propTypes = {
    showIntercom: PropTypes.func,
    trackEvent: PropTypes.func,
    viewer: PropTypes.shape({
      companies: PropTypes.array,
    }),
  };

  componentDidMount() {
    const { trackEvent } = this.props;
    trackEvent('Viewed Salesforce Integration Page');
  }

  onContactUsTapped = () => {
    this.props.showIntercom();
  };

  renderCTA() {
    const { viewer } = this.props;
    if (!viewer || viewer.loggedOut || viewer.companies.length === 0) {
      // logged out || no companies -> register
      return (
        <Link className="cta" to="/register">
          <Button buttonType="greenButton" value="Get started" />
        </Link>
      );
    }

    // logged in && has company -> admin dashboard (assume first company for now)
    return (
      <Button
        buttonType="blackButton"
        className="cta"
        onTap={this.onContactUsTapped}
        value="Contact Us for access"
      />
    );
  }

  render() {
    return (
      <div className="integrationsLanding">
        <SalesforceLandingHelmet />
        <div className="integrationsLandingContainer">
          <section className="header">
            <div className="left">
              <LazyLoadedImage className="appLogo" loading="eager" src={SalesforceLogo} />
              <div className="text">
                <h1 className="title">Canny for Salesforce</h1>
                <div className="subtitle">
                  Track feedback on behalf of contacts in&nbsp;Salesforce
                </div>
                <div className="links">
                  {this.renderCTA()}
                  <a
                    href="https://help.canny.io/en/articles/3808707-salesforce-integration"
                    rel="noopener"
                    target="_blank"
                    className="LandingIntegrations__install-link">
                    See install docs
                  </a>
                </div>
              </div>
            </div>
          </section>
          <section className="features">
            <div className="featureHighlight">
              <div className="featureImage">
                <LazyLoadedImage
                  className="shadow"
                  loading="eager"
                  src={Salesforce1}
                  alt="The Canny widget that lives inside Salesforce, showing previous requests and a button to add a new one."
                />
              </div>
              <div className="featureText">
                <div className="title">Keep track of your prospect's dealbreakers</div>
                <div className="subtitle">
                  Close more deals by staying on top of what prospects are asking for. You'll know
                  the status of these requests from your product&nbsp;team.
                </div>
              </div>
            </div>
            <div className="featureHighlight">
              <div className="featureImage">
                <LazyLoadedImage
                  className="shadow"
                  src={Salesforce2}
                  alt="A form that lets you search for an existing feedback item, or add a new one."
                />
              </div>
              <div className="featureText">
                <div className="title">No need to leave&nbsp;Salesforce</div>
                <div className="subtitle">
                  Send feedback to your product team without disrupting your current workflow.
                  Search and create Canny posts from&nbsp;Salesforce.
                </div>
              </div>
            </div>
            <div className="featureHighlight">
              <div className="featureImage">
                <LazyLoadedImage
                  className="shadow"
                  src={Salesforce3}
                  alt="The breakdown of all feedback and opportunities."
                />
              </div>
              <div className="featureText">
                <div className="title">All your feedback in one&nbsp;place</div>
                <div className="subtitle">
                  Canny keeps track of all votes that came from Salesforce. Link potential revenue
                  opportunities to inform your product&nbsp;decisions.
                </div>
              </div>
            </div>
          </section>
          <LandingFooterCTA style="solid" />
          <FAQs
            faqs={[
              {
                question: 'What is a feedback management system?',
                answer:
                  "<div><p>This is a process of collecting, organizing, and actioning customer feedback. When you gather actionable insights from your users, you end up building better products. At the same time, you boost customer satisfaction. That's because customer experience improves when users feel heard.</p><p>Canny helps you set up an effective feedback management system without leaving Salesforce.</p></div>",
                html: (
                  <>
                    <p>
                      This is a process of collecting, organizing, and actioning customer feedback.
                      When you gather actionable insights from your users, you end up building
                      better products. At the same time, you boost customer satisfaction. That's
                      because customer experience improves when users feel&nbsp;heard.
                    </p>
                    <p>
                      Canny helps you set up an effective feedback management system without
                      leaving&nbsp;Salesforce.
                    </p>
                  </>
                ),
              },
              {
                question: 'How can Canny help with my Salesforce feedback management?',
                answer:
                  "<div><p>Capture customer sentiment in Salesforce CRM. Then, send ideas from your sales prospects directly to Canny. In Canny, you'll be able to track the status of these requests, prioritize them, and more. That feedback data will help your product management.</p><p>Salesforce is usually a home for customer data. Let's keep it this way. You don't need to leave the tool you know and love to manage user feedback.</p></div>",
                html: (
                  <>
                    <p>
                      Salesforce is usually a home for customer data. Let's keep it this way. You
                      don't need to leave the tool you know and love to manage user&nbsp;feedback.
                    </p>
                    <p>
                      Capture customer sentiment in Salesforce CRM. Then, send ideas from your sales
                      prospects directly to Canny. In Canny, you'll be able to track the status of
                      these requests, prioritize them, and more. That feedback data will help your
                      product&nbsp;management.
                    </p>
                  </>
                ),
              },
              {
                question: 'How much does it cost?',
                answer:
                  '<div><p>We have a variety of plans to fit your feedback management needs. Get started with our Free plan - it has everything you need to collect and manage customer feedback. And our Salesforce integration helps you manage it all in one place.</p><p>Note: Salesforce integration is only available on our Business plan.</p></div>',
                html: (
                  <>
                    <p>
                      We have a variety of plans to fit your feedback management needs. Get started
                      with our Free plan - it has everything you need to collect and manage customer
                      feedback. And our Salesforce integration helps you manage it all in one place.
                    </p>
                    <p>Note: Salesforce integration is only available on our Business plan.</p>
                  </>
                ),
              },
              {
                question: 'What other integrations does Canny have?',
                answer:
                  '<div><p>In addition to Salesforce, we also integrate with many other popular tools. For example:</p><ul><li><p>Hubspot</p></li><li><p>Slack</p></li><li><p>Microsoft Teams</p></li><li><p>Intercom</p></li><li><p>Zendesk</p></li><li><p>And many more</p></li></ul><p>Check out the full list of <a href="https://canny.io/integrations" rel="noopener" target="_blank">integrations here</a>.</p></div>',
                html: (
                  <>
                    <p>
                      In addition to Salesforce, we also integrate with many other popular tools.
                      For&nbsp;example:
                    </p>
                    <ul className="orderedList">
                      <li className="orderedListItem">
                        <p className="orderedListItemCopy">Hubspot</p>
                      </li>
                      <li className="orderedListItem">
                        <p className="orderedListItemCopy">Slack</p>
                      </li>
                      <li className="orderedListItem">
                        <p className="orderedListItemCopy">Microsoft Teams</p>
                      </li>
                      <li className="orderedListItem">
                        <p className="orderedListItemCopy">Intercom</p>
                      </li>
                      <li className="orderedListItem">
                        <p className="orderedListItemCopy">Zendesk</p>
                      </li>
                      <li className="orderedListItem">
                        <p className="orderedListItemCopy">And many more</p>
                      </li>
                    </ul>
                    <p>
                      Check out the full list of{' '}
                      <a href="https://canny.io/integrations" rel="noopener" target="_blank">
                        integrations here
                      </a>
                      .
                    </p>
                  </>
                ),
              },
            ]}
          />
        </div>
      </div>
    );
  }
}

export default withContexts({
  showIntercom: ShowIntercomContext,
  trackEvent: TrackEventContext,
  viewer: ViewerContext,
})(SalesforceLanding);
